/**
 * 群組相關業務邏輯服務
 * 封裝所有群組相關的 API 調用
 */

import { buildApiUrl, apiCall } from './apiService';

/**
 * 獲取組織群組列表
 * @param {string} orgCode - 組織代碼（如 "KS", "KCQS"）
 * @param {Function} getAccessToken - 獲取 token 的函數
 * @param {Object} tokenRef - token 的 ref 對象
 * @returns {Promise<Array>} - 群組列表
 */
export const fetchOrgGroups = async (orgCode, getAccessToken, tokenRef) => {
  const url = buildApiUrl(`api/get_org/${orgCode}`);
  const response = await apiCall(url, { method: 'GET' }, getAccessToken, tokenRef);
  
  if (response.ok) {
    return await response.json();
  } else {
    throw new Error(`Failed to fetch org groups: ${response.statusText}`);
  }
};

/**
 * 獲取群組成員列表
 * @param {string} groupId - 群組 ID
 * @param {Function} getAccessToken - 獲取 token 的函數
 * @param {Object} tokenRef - token 的 ref 對象
 * @returns {Promise<Array>} - 成員列表
 */
export const fetchGroupMembers = async (groupId, getAccessToken, tokenRef) => {
  const url = buildApiUrl(`api/get_members/${groupId}`);
  const response = await apiCall(url, { method: 'GET' }, getAccessToken, tokenRef);
  
  if (response.ok) {
    return await response.json();
  } else {
    throw new Error(`Failed to fetch group members: ${response.statusText}`);
  }
};

/**
 * 獲取群組的子群組列表
 * @param {string} groupId - 群組 ID
 * @param {Function} getAccessToken - 獲取 token 的函數
 * @param {Object} tokenRef - token 的 ref 對象
 * @returns {Promise<Array>} - 子群組列表
 */
export const fetchSubgroups = async (groupId, getAccessToken, tokenRef) => {
  const url = buildApiUrl(`api/get_subgroup/${groupId}`);
  const response = await apiCall(url, { method: 'GET' }, getAccessToken, tokenRef);
  
  if (response.ok) {
    return await response.json();
  } else {
    throw new Error(`Failed to fetch subgroups: ${response.statusText}`);
  }
};

/**
 * 獲取群組的遞移成員（包括所有子群組的成員）
 * @param {string} groupId - 群組 ID
 * @param {Function} getAccessToken - 獲取 token 的函數
 * @param {Object} tokenRef - token 的 ref 對象
 * @returns {Promise<Array>} - 遞移成員列表
 */
export const fetchTransitiveMembers = async (groupId, getAccessToken, tokenRef) => {
  const url = buildApiUrl(`api/get_transitive_members/${groupId}`);
  const response = await apiCall(url, { method: 'GET' }, getAccessToken, tokenRef);
  
  if (response.ok) {
    const data = await response.json();
    // 過濾有效的成員（必須有 mail 或 userPrincipalName）
    return data.filter(m => m.mail || m.userPrincipalName);
  } else {
    throw new Error(`Failed to fetch transitive members: ${response.statusText}`);
  }
};

/**
 * 搜尋群組和用戶
 * @param {string} query - 搜尋關鍵字
 * @param {Function} getAccessToken - 獲取 token 的函數
 * @param {Object} tokenRef - token 的 ref 對象
 * @returns {Promise<Array>} - 搜尋結果列表
 */
export const searchGroups = async (query, getAccessToken, tokenRef) => {
  const url = buildApiUrl(`api/search_groups/${encodeURIComponent(query)}`);
  const response = await apiCall(url, { method: 'GET' }, getAccessToken, tokenRef);
  
  if (response.ok) {
    return await response.json();
  } else {
    throw new Error(`Failed to search groups: ${response.statusText}`);
  }
};

/**
 * 獲取群組的子群組和成員（並行獲取）
 * @param {string} groupId - 群組 ID
 * @param {string} deptId - 部門 ID
 * @param {Function} getAccessToken - 獲取 token 的函數
 * @param {Object} tokenRef - token 的 ref 對象
 * @returns {Promise<{subgroups: Array, members: Array, functionalGroups: Array}>} - 子群組和成員
 */
export const fetchGroupData = async (groupId, deptId, getAccessToken, tokenRef) => {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("無法獲取 token");
  }

  // 並行獲取子群組和成員
  const [subgroupResponse, memberResponse, functionalGroupResponse] = await Promise.all([
    fetchSubgroups(groupId, getAccessToken, tokenRef),
    fetchGroupMembers(groupId, getAccessToken, tokenRef),
    fetchOrgGroups(deptId, getAccessToken, tokenRef),
  ]);

  return {
    subgroups: subgroupResponse,
    members: memberResponse,
    functionalGroups: functionalGroupResponse,
  };
};

